import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPatients, fetchAllProfessionals } from '../../../redux/actions/relatedEntitiesActions';
import { fetchConsultationById, putCompleteConsultation } from '../../../redux/actions/consultationActions';
import { fetchPersons } from '../../../redux/actions/personActions';
import { createNewAnthropometry } from '../../../redux/actions/anthropometryActions';
import { clearAnthropometry } from '../../../redux/slices/anthropometrySlice';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import Accordion from '../../../components/Accordion/Accordion';
import ModalForms from '../../../components/ConfirmationModal/ModalForms';
import { generateAnthropometryLog } from '../../../utils/anthropometry/generateAnthropometryLog';
import { formatDate, formatGroup, groupOptions } from '../../../utils/Functions/formatPersons';
import { onlyNumbers, convertDateForDatabase, handleKeyDown } from '../../../utils/Functions/formatAnthropometry';
import { activityFactorCodeMap, selectOptions } from '../../../utils/anthropometry/anthropometryAllFunctions';
import { Tabs, Tab } from '../../../components/Tabs/Tabs';

import {
  calculateBMI,
  calculateTheoreticalWeight,
  calculateEstimatedHeight,
  calculateBMR,
  calculateTEE,
  calculateFatMassWeight,
  calculateLeanBodyMass,
  calculateTotalBodyWater,
  calculateArmMuscleCircumference,
  calculateArmMuscleArea,
  calculateCorrectedArmMuscleArea,
  calculateArmArea,
  calculateArmFatArea,
  calculateEstimatedCurrentWeight,
  calculateWHR,
  calculateResidualMass,
  calculateWeightExcess,
  calculateWeightDeficit,
  calculateLeanWeightType,
  calculateIdealWeight,
  calculateCMB,
  calculateAMB,
  calculateAMBc,
  calculateAge,
  calculateMuscleMassWeight,
} from '../../../utils/anthropometry/calculations';

import {
  classifyMuscleMass,
  classifyBMI,
  classifyBiotype,
  classifyBlackburn,
  classifyBray,
  classifyInternalFatPercentage,
  classifyWaistCircumference,
  classifyActivityFactor,
  classifyActivityLevel,
  classifyChildBMI,
  classifyElderlyBMI,
  classifyLeanBodyMass,
  calculateMuscleDiagnosis,
} from '../../../utils/anthropometry/classifications';

import {
  calculateDPP,
  calculateGestationalAge
} from '../../../utils/anthropometry/dates';

import {
  calculateCompleteBodyFat,
  calculateMuscleMassDiagnosis
} from '../../../utils/anthropometry/formulas';


import '../styles/CreateAnthropometry.css';
import '../../../Mobile.css';

import { clearConsultation } from '../../../redux/slices/consultationSlice';
import { clearAppointment } from '../../../redux/slices/appointmentSlice';
import { clearCurrentPerson } from '../../../redux/slices/relatedEntitiesSlice';
import { ValidatedNumberInput } from './ValidatedNumberInput';
import { useModalFeedback } from '../../../context/ModalContext';
// import Escalometro from '../../../components/Escalometro/Escalometro';

const CreateAnthropometry = () => {
  const location = useLocation();
  const { patient_id } = location.state || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [message, setMessage] = useState('');
  // const [showModal, setShowModal] = useState(false);
  const { showInfo } = useModalFeedback();

  const [selectedPatientGender, setSelectedPatientGender] = useState('');
  const setFieldValueRef = useRef(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Obtendo pacientes, profissionais e estado de antropometria
  const { patients = [], loadingPatients, errorPatients } = useSelector((state) => state.relatedEntities);
  const { professionals = [], loadingProfessionals } = useSelector((state) => state.relatedEntities);
  const { persons = [], loadingPersons } = useSelector((state) => state.persons);
  const { currentConsultation, loading: consultationLoading, error: consultationError } = useSelector(state => state.consultations);


  const [patientBirthDate, setPatientBirthDate] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientGender, setPatientGender] = useState('');
  const [loadedProfessionals, setLoadedProfessionals] = useState({});

  // Variáveis que precisam ser calculadas

  const [height, setHeight] = useState(0);
  const [weight, setWeight] = useState(0);
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [bmi, setBmi] = useState(0);
  const [imcClassificacao, setImcClassificacao] = useState('');
  const [physicalActivityLevel, setPhysicalActivityLevel] = useState('');
  const [frequencyOfActivity, setFrequencyOfActivity] = useState('');
  const [pesoTeorico, setPesoTeorico] = useState(0);
  const [classificacaoBernadinelli, setClassificacaoBernadinelli] = useState('');
  const [classificacaoBlackburn, setClassificacaoBlackburn] = useState('');
  const [classificacaoBray, setClassificacaoBray] = useState('');
  const [kneeHeight, setKneeHeight] = useState(0);
  const [alturaEstimada, setAlturaEstimada] = useState(0);
  const [tmb, setTmb] = useState(0); //Taxa de Metabolismo Basal
  const [vet, setVet] = useState(0); // Valor Energético Total

  const [waistClassification, setWaistClassification] = useState('');
  const [tricepsSkinfold, setTricepsSkinfold] = useState(0);
  const [subscapularSkinfold, setSubscapularSkinfold] = useState('');
  const [supraespinalSkinfold, setSupraespinalSkinfold] = useState('');
  const [chestSkinfold, setChestSkinfold] = useState('');
  const [bicepsSkinfold, setBicepsSkinfold] = useState('');
  const [thighSkinfold, setThighSkinfold] = useState('');
  const [calfSkinfold, setCalfSkinfold] = useState('');
  const [abdominalSkinfold, setAbdominalSkinfold] = useState('');
  const [axillarySkinfold, setAxillarySkinfold] = useState('');
  const [cmb, setCmb] = useState(0);  // Circunferência Muscular do Braço
  const [amb, setAmb] = useState(0);  // Área Muscular do Braço
  const [ambc, setAmbc] = useState(0);  // Área Muscular do Braço Corrigida
  const [selectedFatPercentageEquation, setSelectedFatPercentageEquation] = useState('');
  const [selectedPatientGroup, setSelectedPatientGroup] = useState('');
  const [somaDobrasCutaneas, setSomaDobrasCutaneas] = useState(0);
  const [percentualGordura, setPercentualGordura] = useState(0);
  const [ab, setAb] = useState(0);    // Área do Braço
  const [agb, setAgb] = useState(0);   // Área Gordurosa do Braço
  const [classificacaoMusculatura, setClassificacaoMusculatura] = useState('');
  const [imcZscore, setImcZscore] = useState(0);
  const [showZScoreField, setShowZScoreField] = useState(false);
  const [dum, setDum] = useState(''); //Data da Última Menstruação
  const [idadeGestacional, setIdadeGestacional] = useState(0);
  const [equationText, setEquationText] = useState('');
  const [ppgWeight, setPpgWeight] = useState('');
  const [recommendedWeightGain, setRecommendedWeightGain] = useState('');
  const [gestationalImc, setGestationalImc] = useState('');
  const [dppDate, setDppDate] = useState('');
  const [pesoMassaGorda, setPesoMassaGorda] = useState('');
  const [diagnosticoMassaMuscular, setDiagnosticoMassaMuscular] = useState('');
  const [percentualIdealGordura, setPercentualIdealGordura] = useState('');
  const [percentualGorduraInterna, setPercentualGorduraInterna] = useState('');
  const [totalAguaCorporal, setTotalAguaCorporal] = useState('');
  const [massaCorporalMagra, setMassaCorporalMagra] = useState('');
  const [erroGrupoGestante, setErroGrupoGestante] = useState('');
  const [patientMonth, setPatientMonth] = useState('');
  const [abdominalCircumference, setAbdominalCircumference] = useState(0);
  const [ankleCircumference, setAnkleCircumference] = useState(0);
  const [armCircumference, setArmCircumference] = useState(0);
  const [calfCircumference, setCalfCircumference] = useState(0);
  const [chestCircumference, setChestCircumference] = useState(0);
  const [forearmCircumference, setForearmCircumference] = useState(0);
  const [glutealMaxCircumference, setGlutealMaxCircumference] = useState(0);
  const [headCircumference, setHeadCircumference] = useState(0);
  const [hipCircumference, setHipCircumference] = useState(0);
  const [thighMaxCircumference, setThighMaxCircumference] = useState(0);
  const [waistCircumference, setWaistCircumference] = useState(0);
  const [physicalActivityClassification, setPhysicalActivityClassification] = useState('');
  const [leanWeightType, setLeanWeightType] = useState('');
  const [overweightDuration, setOverweightDuration] = useState(0);
  const [perdaDePesoRecente, setPerdaDePesoRecente] = useState(0);

  // estados parcialmente utilizados

  const [suprailiacSkinfold, setSuprailiacSkinfold] = useState('');
  const [selectedPatientCategory, setSelectedPatientCategory] = useState('');
  const [pesoMassaCorporal, setPesoMassaCorporal] = useState('');
  const [physicalActivityFrequency, setPhysicalActivityFrequency] = useState('');
  const [imc, setImc] = useState(0);
  const [diagnosis, setDiagnosis] = useState('');
  const [patientYear, setPatientYear] = useState('');
  const [porcentagemPT, setPorcentagemPT] = useState(0);
  const [wristCircumference, setWristCircumference] = useState(0);
  const [leftArmCircumference, setLeftArmCircumference] = useState(0);
  const [rightArmCirumference, setRightArmCircumference] = useState(0);
  const [leftForeArmCircumference, setLeftForeArmCircumference] = useState(0);
  const [rightForeArmCircumference, setRightForeArmCircumference] = useState(0);
  const [leftHandCircumference, setLeftHandCircumference] = useState(0);
  const [rightHandCircumference, setRightHandCircumference] = useState(0);
  const [dpp, setDpp] = useState(0);
  const [giPercentageType, setGiPercentageType] = useState(0);
  const [estimatedCurrentWeight, setEstimatedCurrentWeight] = useState(0);
  const [estimatedWeight, setEstimatedWeight] = useState(0);
  const [referenceEstimatedWeight, setReferenceEstimatedWeight] = useState(0);
  const [excessoPeso, setExcessoPeso] = useState(0);
  const [deficitPeso, setDeficitPeso] = useState(0);
  const [massaResidual, setMassaResidual] = useState(0);
  const [relacaoCinturaQuadril, setRelacaoCinturaQuadril] = useState(0);
  const [vetFinal, setVetFinal] = useState(0);
  const [vetFinalEquation, setVetFinalEquation] = useState(0);
  const [vetFinalWeight, setVetFinalWeight] = useState(0);
  const [vetReductionId, setVetReductionId] = useState(0);



  // estados que ainda não estão sendo utilizados
  const [pesoEstimado, setPesoEstimado] = useState('');
  const [pesoAtualEstimado, setPesoAtualEstimado] = useState('');



  useEffect(() => {
    if (!location.state) {
      dispatch(clearConsultation());
      dispatch(clearAppointment());
      dispatch(clearCurrentPerson());
    }
    setInitialLoadComplete(true); // Libera o carregamento do restante
  }, [location.state, dispatch]);




  // Carregar pacientes, pessoas e profissionais ao montar o componente
  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchPersons());
    dispatch(fetchAllProfessionals());
    dispatch(clearAnthropometry());
  }, [dispatch]);

  // Atualizar o estado de loadedProfessionals quando persons ou professionals mudarem
  useEffect(() => {
    if (!loadingPersons && !loadingProfessionals && persons.length > 0 && professionals.length > 0) {
      const professionalMap = {};

      // Mapeia todos os profissionais com base no person_id
      // biome-ignore lint/complexity/noForEach: <explanation>
      persons.forEach(person => {
        if (['nutritionist', 'professional', 'doctor'].includes(person.person_type)) {
          professionalMap[person.person_id] = `${person.first_name} ${person.last_name}`;
        }
      });

      // Adiciona ou atualiza com informações adicionais dos professionals, se necessário
      // biome-ignore lint/complexity/noForEach: <explanation>
      professionals.forEach(professional => {
        if (!professionalMap[professional.person_id]) {
          professionalMap[professional.person_id] = professional.name || 'Nome não disponível';
        }
      });

      setLoadedProfessionals(professionalMap);
      // console.log('Profissionais mapeados:', professionalMap);
    }
  }, [loadingPersons, loadingProfessionals, persons, professionals]);

  // Insere o paciente vindo da consulta
  useEffect(() => {
    if (patient_id && patients.length > 0) {
      const matchedPatient = patients.find((p) => p.person_id === patient_id);
      if (matchedPatient) {
        const initialOption = {
          value: matchedPatient.person_id,
          label: matchedPatient.name,
        };
        setSelectedPatient(initialOption); // Atualiza o estado do paciente selecionado
      }
    }
  }, [patient_id, patients]);

  // Selecionando um Paciente
  const handlePatientChange = (option, setFieldValue) => {
    const patientId = option?.value || '';
    setFieldValue('patient_id', patientId);

    if (!option) {
      setSelectedPatient(null);
      setSelectedPatientGender('');
      setGender('');
      setPatientBirthDate('');
      setAge('');
      return;
    }

    const selectedPatient = persons.find(p => p.person_id === patientId && p.person_type === 'patient');
    if (selectedPatient) {
      setSelectedPatient(selectedPatient);
      const gender = selectedPatient.gender || '';
      setSelectedPatientGender(gender);
      setGender(gender);

      if (selectedPatient.birth_date) {
        const birth = selectedPatient.birth_date;

        // ⚠️ Aqui mantemos o formato ISO no estado
        setPatientBirthDate(birth);

        // ✅ Calcular idade diretamente a partir de birth
        const completeAge = calculateAge(birth);
        if (completeAge) {
          setAge(completeAge.years);
          setPatientYear(completeAge.years);
          setPatientMonth(completeAge.months);

          // console.log("✅ Idade calculada:", completeAge.years, "anos e", completeAge.months, "meses");
        }
      }
    } else {
      console.warn('Paciente não encontrado na lista de pacientes.');
    }
  };

  useEffect(() => {
    if (!currentConsultation && selectedPatient && weight && height && gender) {
      console.log('🔁 Executando cálculos para antropometria direta');
      // Acione manualmente os cálculos ou atualize estados-chave como `bmi`, `imcClassificacao`, etc.
    }
  }, [currentConsultation, selectedPatient, weight, height, gender]);

  // Curent Consultation
  useEffect(() => {
    if (!initialLoadComplete || !setFieldValueRef.current) return;

    if (!currentConsultation) {
      // Limpa os campos manualmente
      setFieldValueRef.current('patient_id', '');
      setFieldValueRef.current('nutritionist_id', '');
      setSelectedPatient(null);
      setSelectedPatientGender('');
      setGender('');
      setSelectedPatientGroup('');
      setShowZScoreField(false);
    } else {
      if (currentConsultation.patient_id) {
        const selectedPatient = patients.find(p => p.person_id === currentConsultation.patient_id);
        const option = selectedPatient ? {
          value: selectedPatient.person_id,
          label: selectedPatient.name
        } : null;

        if (option) {
          setFieldValueRef.current('patient_id', option.value);
          handlePatientChange(option, setFieldValueRef.current);
        }
      }

      if (currentConsultation.professional_id) {
        setFieldValueRef.current('nutritionist_id', currentConsultation.professional_id);
      }
    }
  }, [initialLoadComplete, currentConsultation, patients]);


  const initialValues = {
    ab: '',
    abdominal_circumference: '',
    abdominal_skinfold: '',
    activity_factor_code: '',
    agb: '',
    ankle_circumference: '',
    arm_circumference: '',
    arm_muscle_area: '',
    axillary_skinfold: '',
    biceps_skinfold: '',
    bmi: '',
    body_fat_percentage: '',
    calf_circumference: '',
    calf_skinfold: '',
    category_id: '',
    chest_circumference: '',
    chest_skinfold: '',
    cmb: '',
    consultation_id: '',
    current_weight: '',
    desired_weight: '',
    diagnosis: '',
    estimated_current_weight: '',
    estimated_height: '',
    estimated_weight: '',
    fat_calculate_equation: '',
    fat_mass_weight: '',
    femur_diameter: '',
    forearm_circumference: '',
    gi_percentage_type: '',
    gluteal_max_circumference: '',
    head_circumference: '',
    height: '',
    hip_circumference: '',
    humeral_diameter: '',
    ideal_fat_percentage: '',
    imc: '',
    knee_height: '',
    last_period_date: '',
    lean_weight_type: '',
    left_arm: '',
    left_foot: '',
    left_forearm: '',
    left_hand: '',
    left_leg: '',
    left_lower_leg: '',
    measurement_date: '',
    measurement_type: '',
    muscle_mass_diagnosis: '',
    muscle_mass_weight: '',
    nutritionist_id: '',
    overweight_duration: '',
    patient_group_id: '',
    patient_id: '',
    physical_activity: '',
    physical_activity_frequency: '',
    physical_activity_level: '',
    ppg_weight: '',
    ppr_time: '',
    radial_diameter: '',
    recommended_weight_gain: '',
    reference_estimated_weight: '',
    right_arm: '',
    right_foot: '',
    right_forearm: '',
    right_hand: '',
    right_leg: '',
    right_lower_leg: '',
    subscapular_skinfold: '',
    sum_skinfolds: '',
    supraespinal_skinfold: '',
    suprailiac_skinfold: '',
    theoretical_weight_percentage: '',
    thigh_max_circumference: '',
    thigh_skinfold: '',
    tmb: '',
    total_body_water: '',
    triceps_skinfold: '',
    usual_weight: '',
    vet: '',
    vet_final_weight: '',
    waist_circumference: '',
    waist_classification: '',
    wrist_circumference: '',
    zscore: ''
  };


  const validationSchema = Yup.object({
    patient_id: Yup.number().required('Paciente é necessário'),
    nutritionist_id: Yup.number().required('Nutricionista é necessário'),

    height: Yup.number()
      .required('Altura é necessária')
      .min(40, 'Altura mínima de 40 cm')
      .max(250, 'Altura máxima de 250 cm'),

    current_weight: Yup.number()
      .required('Peso atual é necessário')
      .min(0.5, 'Peso mínimo de 0.5 kg')
      .max(300, 'Peso máximo de 300 kg'),

    usual_weight: Yup.number()
      .required('Peso habitual é necessário')
      .min(2, 'Peso mínimo de 2 kg')
      .max(500, 'Peso máximo de 500 kg'),

    overweight_duration: Yup.number()
      .max(100, 'Peso máximo de 100 anos'),

    measurement_date: Yup.date().
      required('Data da medição é necessária'),

    patient_group_id: Yup.string()
      .required('Grupo do Paciente é necessário'),

    physical_activity_frequency: Yup.string()
      .required('Frequência da Atividade é necessária'),

    physical_activity_level: Yup.string()
      .required('Nível da Atividade é necessário'),

    knee_height: Yup.number()
      .required('Altura do Joelho é necessária')
      .min(20, 'Altura mínima de 20 cm')
      .max(60, 'Altura máxima de 60 cm'),

    fat_calculate_equation: Yup.number().required('Definir uma fórmula é necessário'),

    arm_circumference: Yup.number()
      .required('Circunferência do Braço é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    left_arm: Yup.number()
      .required('Mediada Braço Esquerdo é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_arm: Yup.number()
      .required('Mediada Braço Direito é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    left_forearm: Yup.number()
      .required('Mediada Antebraço Esquerdo é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_forearm: Yup.number()
      .required('Mediada Antebraço Direito é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    left_hand: Yup.number()
      .required('Mediada Mão Esquerda é necessária')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_hand: Yup.number()
      .required('Mediada Mão Direita é necessária')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    subscapular_skinfold: Yup.number()
      .required('Dobra Subescapular é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    chest_skinfold: Yup.number()
      .required('Dobra Peitoral é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    triceps_skinfold: Yup.number()
      .required('Dobra Tríceps é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    biceps_skinfold: Yup.number()
      .required('Dobra Bíceps é necessária')
      .min(2, 'Mínimo 2 mm')
      .max(70, 'Máximo 70 mm'),

    left_leg: Yup.number()
      .required('Mediada Perna Esquerda é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    left_lower_leg: Yup.number()
      .required('Mediada Perna Esquerda é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_leg: Yup.number()
      .required('Mediada Perna Direita é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_lower_leg: Yup.number()
      .required('Mediada Perna Direita é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    left_foot: Yup.number()
      .required('Mediada Pé Esquerdo é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    right_foot: Yup.number()
      .required('Mediada Pé Direito é necessária, se preciso use 0')
      .min(0, 'Mínimo 0 cm')
      .max(200, 'Máximo 200 cm'),

    thigh_skinfold: Yup.number()
      .required('Dobra Coxa é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    calf_skinfold: Yup.number()
      .required('Dobra Panturrilha é necessária')
      .min(2, 'Mínimo 2 mm')
      .max(70, 'Máximo 70 mm'),

    abdominal_skinfold: Yup.number()
      .required('Dobra Abdominal é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    axillary_skinfold: Yup.number()
      .required('Dobra Axilar é necessária')
      .min(5, 'Mínimo 5 mm')
      .max(70, 'Máximo 70 mm'),

    wrist_circumference: Yup.number()
      .required('Circunferência do Pulso é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    head_circumference: Yup.number()
      .required('Circunferência da Cabeça é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    chest_circumference: Yup.number()
      .required('Circunferência do Tórax é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    abdominal_circumference: Yup.number()
      .required('Circunferência Abdominal é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    hip_circumference: Yup.number()
      .required('Circunferência do Quadril é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    thigh_max_circumference: Yup.number()
      .required('Circunferência da Coxa é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    calf_circumference: Yup.number()
      .required('Circunferência da Panturrilha é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    ankle_circumference: Yup.number()
      .required('Circunferência do Tornozelo é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(100, 'Máximo 100 cm'),

    forearm_circumference: Yup.number()
      .required('Circunferência do Antebraço é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    gluteal_max_circumference: Yup.number()
      .required('Circunferência Glútea é necessária')
      .min(10, 'Mínimo 10 cm')
      .max(200, 'Máximo 200 cm'),

    desired_weight: Yup.number()
      .min(2, 'Mínimo 2 kg')
      .max(300, 'Máximo 300 kg'),

    supraespinal_skinfold: Yup.number()
      .required('Dobra Supraespinal é necessária')
      .min(2, 'Mínimo 2 mm')
      .max(70, 'Máximo 70 mm'),

    suprailiac_skinfold: Yup.number()
      .required('Dobra Suprailiaca é necessária')
      .min(2, 'Mínimo 2 mm')
      .max(70, 'Máximo 70 mm'),

    waist_circumference: Yup.number()
      .required('Circunferência da Cintura é necessária')
      .min(40, 'Mínimo 40 cm')
      .max(150, 'Máximo 150 cm'),

    femur_diameter: Yup.number()
      .typeError('Por favor insira um valor numérico')
      .min(20, 'Valores abaixo de 30cm não são anatomicamente plausíveis para adultos')
      .max(65, 'Valores acima de 65cm exigem confirmação da medição')
      .test(
        'typical-range',
        'Valor fora da faixa típica (20-60cm) - verifique se a medição está correta',
        value => !value || (value >= 20 && value <= 60)
      ),

    humeral_diameter: Yup.number()
      .required('Diâmetro Umeral é necessário')
      .min(40, 'Mínimo 40 cm')
      .max(150, 'Máximo 150 cm'),

    radial_diameter: Yup.number()
      .required('Diâmetro Radial é necessário')
      .min(12, 'Mínimo 12 cm')
      .max(45, 'Máximo 45 cm'),

  });

  // ......................................Funções da Antropometria
  // Função para calcular a Data Prevista do Parto (DPP)
  const calcularDPP = useCallback((dum) => {
    if (!dum) return null;

    const dataDUM = new Date(dum);
    const dataDPP = new Date(dataDUM.setDate(dataDUM.getDate() + 280)); // Adiciona 280 dias (40 semanas)

    return formatDate(dataDPP) // Formata a data para o formato local
  }, []);

  // Função para calcular a Idade Gestacional (IG)
  const calcularIdadeGestacional = useCallback((dum) => {
    if (!dum) return null;

    const hoje = new Date();
    const dataDUM = new Date(dum);

    if (isNaN(dataDUM.getTime())) {
      console.error('Data de DUM inválida');
      return null;
    }

    // Calcula a diferença em semanas
    const diferencaTempo = hoje - dataDUM;
    const idadeGestacional = Math.floor(diferencaTempo / (1000 * 60 * 60 * 24 * 7)); // Convertendo milissegundos para semanas

    return idadeGestacional;
  }, []);

  //Função para Calcular a Idade Gestacional e a Data Prevista do Parto
  const handleGestanteCalculations = useCallback((dum) => {
    if (selectedPatientGroup === '5') {  // Verifica se o grupo é "Gestante"
      const idadeGestacional = calcularIdadeGestacional(dum);
      setIdadeGestacional(idadeGestacional);  // Define a Idade Gestacional no estado

      const dppCalculada = calcularDPP(dum);
      setDpp(dppCalculada);  // Define a DPP no estado
    }
  }, [selectedPatientGroup, calcularIdadeGestacional, calcularDPP]);


  // useEffect to Validate Only Female Patients Can Be Pregnant/Lactating
  useEffect(() => {
    if (!gender || !selectedPatientGroup) return;

    // Check if male patient is selected as pregnant (5) or lactating (6)
    if (gender === 'male' && (selectedPatientGroup === '5' || selectedPatientGroup === '6')) {
      setErroGrupoGestante("Pacientes do sexo masculino não podem ser classificados como Gestantes/Lactantes");

      // Reset all pregnancy-related fields
      setDum(null);
      setIdadeGestacional(null);
      setDpp(null);
      setGestationalImc(null);
      setPpgWeight(null);
      setRecommendedWeightGain(null);

      // Also reset the patient group in the form
      setFieldValueRef.current('patient_group_id', '');
      setSelectedPatientGroup('');
    } else {
      setErroGrupoGestante('');
    }
  }, [gender, selectedPatientGroup, setFieldValueRef.current]);

  // useEffect dos Cálculos Antropométricos 
  useEffect(() => {
    const heightCm = Number(height);
    const heightM = heightCm / 100;
    const currentWeight = Number(weight);
    const ageNumber = Number(age);
    const kneeHeightValue = Number(kneeHeight);
    const armCirc = Number(armCircumference);
    const triceps = Number(tricepsSkinfold);


    const skinfolds = {
      triceps: tricepsSkinfold,
      suprailiac: suprailiacSkinfold,
      subscapular: subscapularSkinfold,
      chest: chestSkinfold,
      axillary: axillarySkinfold,
      abdominal: abdominalSkinfold,
      thigh: thighSkinfold,
      calf: calfSkinfold,
      biceps: bicepsSkinfold,
    };

    // 1. Initial validation
    if (!heightCm || !currentWeight || !gender || !ageNumber || !selectedPatientGroup) return;

    // 2. Gender x Pregnancy group validation
    if ((gender === 'male') && (selectedPatientGroup === '5' || selectedPatientGroup === '6')) {
      setErroGrupoGestante("The selected patient's gender cannot be Pregnant / Lactating.");
      setDum(null);
      setIdadeGestacional(null);
      setDpp(null);
      setGestationalImc(null);
      setPpgWeight(null);
      setRecommendedWeightGain(null);
    } else {
      setErroGrupoGestante('');
    }

    // 3. BMI
    const bmiValue = calculateBMI(heightM, currentWeight);
    setImc(bmiValue);
    setBmi(bmiValue);

    const bmiClass = classifyBMI(bmiValue, imcZscore, selectedPatientGroup);
    setImcClassificacao(bmiClass);
    //3.a Child BMI / Elderly BMI
    if (selectedPatientGroup === '2') {
      const childBmiClass = classifyChildBMI(bmiValue, ageNumber, gender);
      setImcClassificacao(childBmiClass);
    } else if (selectedPatientGroup === '4') {
      const elderlyBmiClass = classifyElderlyBMI(bmiValue, ageNumber, gender);
      setImcClassificacao(elderlyBmiClass);
    }

    // 4. Estimated height
    if (kneeHeightValue) {
      const estimated = calculateEstimatedHeight(ageNumber, kneeHeightValue, gender);
      setAlturaEstimada(estimated);
    }

    // 5. Theoretical weight
    const theoreticalWeight = calculateTheoreticalWeight(heightCm, gender);
    setPesoTeorico(theoreticalWeight);

    // 6. Biotype
    const biotype = classifyBiotype(heightCm, gender);
    setClassificacaoBernadinelli(biotype);

    // 7. %PT and Blackburn
    if (theoreticalWeight) {
      const ptPercentage = ((currentWeight / theoreticalWeight) * 100).toFixed(2);
      setPorcentagemPT(ptPercentage);
      setClassificacaoBlackburn(classifyBlackburn(ptPercentage));
    }

    // 8. Bray classification
    const bray = classifyBray(bmiValue);
    setClassificacaoBray(bray);

    // 9. Physical Activity - Factor and Frequency
    if (physicalActivityLevel && gender) {
      const factor = classifyActivityFactor(physicalActivityLevel, gender);
      setPhysicalActivityFrequency(factor);
    }

    if (frequencyOfActivity) {
      const level = classifyActivityLevel(frequencyOfActivity);
      setPhysicalActivityClassification(level);
    }

    // 10. BMR and TEE
    const determineVetFinalWeight = (preferred, fallback1, fallback2) => {
      const val1 = Number(preferred);
      if (!isNaN(val1) && val1 > 0) return val1;

      const val2 = Number(fallback1);
      if (!isNaN(val2) && val2 > 0) return val2;

      const val3 = Number(fallback2);
      if (!isNaN(val3) && val3 > 0) return val3;

      return null;
    };
    const bmr = calculateBMR(gender, theoreticalWeight, currentWeight, heightM, ageNumber);
    if (bmr > 0) {
      setTmb(bmr);
      const tee = calculateTEE(bmr, physicalActivityClassification);
      setVet(tee);
      setVetFinal(tee);
      setVetFinalWeight(determineVetFinalWeight(tee, currentWeight, theoreticalWeight));// ou currentWeight se for o usado
      setVetFinalEquation('FAO/OMS/1985'); // ou um código, se preferir
      setVetReductionId(null);
    }

    // 11. Peso da Massa Corporal
    const muscleMassWeight = calculateMuscleMassWeight(massaCorporalMagra, totalAguaCorporal, massaResidual);
    setPesoMassaCorporal(muscleMassWeight);


    // 12. Arm measurements
    if (armCirc && triceps) {
      setCmb(calculateArmMuscleCircumference(armCirc, triceps));
      const ama = calculateArmMuscleArea(armCirc, triceps);
      setAmb(ama);
      setAmbc(calculateCorrectedArmMuscleArea(armCirc, triceps, gender));

      const aa = calculateArmArea(armCirc);
      setAb(aa);
      const afa = calculateArmFatArea(aa, ama);
      setAgb(afa);

      const muscleClass = classifyMuscleMass(ama, gender);
      setClassificacaoMusculatura(muscleClass);
    }

    // 13. Gestational calculations
    if (selectedPatientGroup === '5' && dum) {
      setDppDate(calculateDPP(dum));
      setIdadeGestacional(calculateGestationalAge(dum));
    }

    if (ppgWeight && heightM) {
      const gestationalBMI = (ppgWeight / (heightM ** 2)).toFixed(2);
      setGestationalImc(gestationalBMI);
    }

    // 14. Body fat % and MCM
    if (selectedFatPercentageEquation && ageNumber && gender) {
      const result = calculateCompleteBodyFat(skinfolds, selectedFatPercentageEquation, gender, ageNumber);
      // console.log('🧪 Resultado do cálculo de gordura:', result);

      const { sum, percentage, idealRange } = result;
      setSomaDobrasCutaneas(sum);
      setPercentualGordura(percentage);

      if (idealRange) {
        setPercentualIdealGordura(`${idealRange.min}% - ${idealRange.max}%`);
      }

      // console.log(`🎯 Resultados: Soma = ${sum} Percentual = ${percentage} Faixa Ideal = ${idealRange?.min} - ${idealRange?.max}`);
    }

    // Lean Body Mass Classification
    const classifyLeanBodyMass = (leanMass, gender) => {
      if (gender === 'male') return leanMass < 50 ? 'Low' : 'Normal';
      return leanMass < 40 ? 'Low' : 'Normal';
    };

    // 15. Fat Mass, Lean Mass, Water
    if (height && percentualGordura) {
      const fatMass = calculateFatMassWeight(height, percentualGordura);
      setPesoMassaGorda(fatMass);

      const leanMass = calculateLeanBodyMass(weight, fatMass);
      setMassaCorporalMagra(leanMass);

      setTotalAguaCorporal(calculateTotalBodyWater(leanMass));

      if (massaCorporalMagra && gender) {
        const leanClass = classifyLeanBodyMass(massaCorporalMagra, gender);
        setLeanWeightType(leanClass);
      }

      setLeanWeightType(calculateLeanWeightType(leanMass, gender));

    }

    // 16. Muscle mass diagnosis
    if (classificacaoMusculatura && bmiValue && percentualGordura) {
      const muscleDiagnosis = calculateMuscleMassDiagnosis(bmiValue, percentualGordura, classificacaoMusculatura);
      setDiagnosticoMassaMuscular(muscleDiagnosis);
    }

    // 17. Internal fat %
    if (percentualGordura) {
      setPercentualGorduraInterna(classifyInternalFatPercentage(percentualGordura));
      setGiPercentageType(classifyInternalFatPercentage(percentualGordura));
    }

    // 18. Patient age
    const completeAge = calculateAge(patientBirthDate);
    if (completeAge) {
      setPatientYear(completeAge.years);
      setPatientMonth(completeAge.months);
    }

    // 19. Ideal Weight, Estimated Current Weight, WHR, Residual Mass, Excess and Deficit
    if (height && alturaEstimada && weight && massaCorporalMagra && pesoMassaGorda) {
      const estimatedCurrentWeight = calculateEstimatedCurrentWeight(alturaEstimada); // Altura estimada usada aqui
      setEstimatedCurrentWeight(estimatedCurrentWeight);

      const idealWeight = calculateIdealWeight(height);
      setEstimatedWeight(idealWeight);

      const excess = calculateWeightExcess(weight, idealWeight);
      setExcessoPeso(excess);

      const deficit = calculateWeightDeficit(weight, idealWeight);
      setDeficitPeso(deficit);

      const residualMass = calculateResidualMass(weight, pesoMassaGorda, massaCorporalMagra);
      setMassaResidual(residualMass);
    }

    // 20. Waist classification
    const waistClass = classifyWaistCircumference(waistCircumference, gender);
    setWaistClassification(waistClass);
    // console.log('🟡 Classificação Cintura:', waistCircumference, gender, waistClassification);


    // 21. Waist-Hip Ratio (WHR)
    if (waistCircumference && hipCircumference) {
      const whr = calculateWHR(waistCircumference, hipCircumference);
      setRelacaoCinturaQuadril(whr);
    }


    //Calculate CMB / AMB / AMBc
    if (armCircumference && tricepsSkinfold) {
      const calculatedCMB = calculateCMB(armCircumference, tricepsSkinfold);
      const calculatedAMB = calculateAMB(armCircumference, tricepsSkinfold);
      const calculatedAMBc = calculateAMBc(calculatedAMB);

      setCmb(calculatedCMB);
      setAmb(calculatedAMB);
      setAmbc(calculatedAMBc);
    }



  }, [height, weight, age, gender, selectedPatientGroup, selectedPatientCategory,
    physicalActivityLevel, frequencyOfActivity, armCircumference, tricepsSkinfold,
    suprailiacSkinfold, subscapularSkinfold, chestSkinfold, axillarySkinfold,
    abdominalSkinfold, thighSkinfold, calfSkinfold, bicepsSkinfold, kneeHeight,
    imcZscore, ppgWeight, dum, patientBirthDate, selectedFatPercentageEquation,
    percentualGordura, pesoMassaGorda, massaCorporalMagra, classificacaoMusculatura,
    physicalActivityClassification, alturaEstimada, hipCircumference, waistClassification,
    totalAguaCorporal, massaResidual, waistCircumference]);

  if (loadingPatients) {
    return <div>Carregando...</div>;
  }

  if (errorPatients) {
    return <div>Erro ao carregar dados: {errorPatients}</div>;
  }



  const tabFieldMapping = {
    'Nessa Medição': ['paciente_id', 'nutritionist_id', 'patient_group_id', 'zscore',
      'measurement_date', 'height', 'current_weight', 'desired_weight', 'usual_weight',
      'measurement_type', 'overweight_duration', 'ppr_time', 'physical_activity_frequency',
      'physical_activity_level', 'physical_activity', 'activity_factor_code', 'age',
      'age_years', 'age_months', 'last_period_date', 'ddp_date', 'gestational_imc',
      'gestational_age', 'ppg_weight',
      'recommended_weight_gain', 'weight_excess', 'weight_deficit', 'excess_weight'],
    '2 - Circunferências': ['head_circumference', 'arm_circumference', 'waist_circumference', 'hip_circumference'],
    '3 - Braços / Pernas': ['left_arm', 'right_arm', 'left_forearm', 'right_forearm', 'left_hand', 'right_hand',
      'left_leg', 'right_leg', 'left_lower_leg', 'right_lower_leg', 'left_foot', 'right_foot'],
    '4 - Diâmetros Ósseos': ['knee_height'],
    '5 - Dobras Cutâneas': ['supraespinal_skinfold', 'suprailiac_skinfold', 'subscapular_skinfold', 'triceps_skinfold',
      'chest_skinfold', 'axillary_skinfold', 'abdominal_skinfold', 'thigh_skinfold', 'calf_skinfold', 'biceps_skinfold'],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // console.log(generateAnthropometryLog({
    //   height, weight, age, patientYear, patientMonth, gender, selectedPatientGroup, selectedPatientCategory,
    //   imc, bmi, imcClassificacao, frequencyOfActivity, physicalActivityClassification, physicalActivityLevel,
    //   physicalActivityFrequency, pesoTeorico, classificacaoBernadinelli, porcentagemPT, classificacaoBlackburn,
    //   classificacaoBray, kneeHeight, alturaEstimada, tmb, vet, waistClassification, waistCircumference,
    //   cmb, ankleCircumference, abdominalCircumference, amb, ambc, armCircumference, tricepsSkinfold,
    //   supraespinalSkinfold, somaDobrasCutaneas, percentualGordura, equationText, ab, agb,
    //   classificacaoMusculatura, dum, dppDate, idadeGestacional, ppgWeight, recommendedWeightGain,
    //   gestationalImc, perdaDePesoRecente, pesoMassaGorda, pesoMassaCorporal, percentualIdealGordura,
    //   percentualGorduraInterna, massaCorporalMagra, totalAguaCorporal, diagnosticoMassaMuscular,
    //   diagnosis, massaResidual, selectedFatPercentageEquation, estimatedWeight, estimatedCurrentWeight,
    //   excessoPeso, deficitPeso, referenceEstimatedWeight, relacaoCinturaQuadril, leanWeightType
    // }));

    // console.log("🔍 Contexto atual:", {
    //   patient_id: values.patient_id,
    //   consultation_id: currentConsultation?.consultation_id,
    //   selectedPatient,
    //   selectedPatientGroup,
    //   selectedPatientCategory,
    // });


    const toNumber = (value) => {
      const n = Number(value);
      return isNaN(n) ? null : n;
    };

    try {
      const payload = {
        ...values,

        // IDADE
        age: toNumber(age) || 0,
        age_years: toNumber(patientYear) || 0,
        age_months: toNumber(patientMonth) || 0,

        // MEDIDAS E COMPOSIÇÕES
        ab: toNumber(ab),
        agb: toNumber(agb),
        height: toNumber(height),
        weight: toNumber(weight),
        waist_circumference: toNumber(waistCircumference),
        whr: toNumber(relacaoCinturaQuadril),
        imc: toNumber(imc),
        bmi: toNumber(bmi),
        tmb: toNumber(tmb),
        vet: toNumber(vet),
        cmb: toNumber(cmb),
        arm_muscle_area: toNumber(amb),
        arm_muscle_area_adj: toNumber(ambc),
        arm_area: toNumber(ab),
        arm_fat_area: toNumber(agb),
        fat_mass_weight: toNumber(pesoMassaGorda),
        lean_body_mass_mcm: toNumber(massaCorporalMagra),
        muscle_mass_weight: toNumber(pesoMassaCorporal),
        total_body_water: toNumber(totalAguaCorporal),
        residual_mass: toNumber(massaResidual),


        // DOBRAS CUTÂNEAS E GORDURA
        sum_skinfolds: toNumber(somaDobrasCutaneas),
        body_fat_percentage: toNumber(percentualGordura),
        fat_calculate_equation: selectedFatPercentageEquation,
        fat_calculate_equation_text: equationText,
        fat_calculate_equation_code: selectedFatPercentageEquation,

        // CLASSIFICAÇÕES
        bmi_classification_code: imcClassificacao,
        pg_classification_code: classificacaoBernadinelli,
        blackburn_classification: classificacaoBlackburn,
        bray_classification: classificacaoBray,
        muscle_mass_classification: classificacaoMusculatura,
        muscle_mass_diagnosis: diagnosticoMassaMuscular,
        gi_percentage_type: percentualGorduraInterna,
        lean_weight_type: leanWeightType,

        waist_classification: waistClassification || null,

        // PESOS TEÓRICOS / EXCESSO / DÉFICIT
        theoretical_weight_percentage: toNumber(porcentagemPT),
        estimated_weight: toNumber(estimatedWeight),
        estimated_current_weight: toNumber(estimatedCurrentWeight),
        weight_excess: toNumber(excessoPeso),
        weight_deficit: toNumber(deficitPeso),
        reference_estimated_weight: toNumber(referenceEstimatedWeight) || null,   // --não está declarado ou não há field 


        // ALTURAS E CÁLCULOS ADICIONAIS
        knee_height: toNumber(kneeHeight),
        estimated_height: toNumber(alturaEstimada),

        // GESTAÇÃO (caso aplicável)
        ppg_weight: toNumber(ppgWeight),
        gestational_imc: toNumber(gestationalImc),
        recommended_weight_gain: toNumber(recommendedWeightGain),
        gestational_age: toNumber(idadeGestacional),
        last_period_date: convertDateForDatabase(dum) || null,
        ddp_date: convertDateForDatabase(dppDate) || null,
        // overweight_duration: toNumber(overweightDuration) || null,

        // PERCENTUAL IDEAL
        ideal_fat_percentage: percentualIdealGordura,

        // CLASSIFICAÇÃO DE ATIVIDADE FÍSICA
        physical_activity: physicalActivityFrequency,
        physical_activity_level: physicalActivityLevel,
        physical_activity_frequency: frequencyOfActivity,


        // RELAÇÕES
        patient_id: toNumber(values.patient_id) || currentConsultation?.patient_id || selectedPatient?.person_id || null,
        patient_group_id: toNumber(selectedPatientGroup),
        category_id: toNumber(selectedPatientCategory) || 1,
        nutritionist_id: toNumber(values.nutritionist_id) || currentConsultation?.nutritionist_id || null,



        // PESOS EXTRAS
        current_weight: toNumber(weight),
        desired_weight: toNumber(values.desired_weight),
        usual_weight: toNumber(values.usual_weight),
        measurement_date: values.measurement_date,
        measurement_type: values.measurement_type || '',
        consultation_id: currentConsultation?.consultation_id || null,
        diagnosis: diagnosis || '',

        // NOVO: tipo de peso usado no cálculo da TMB
        bmr_source_weight_type: age <= 10 ? 'current' : 'theoretical', //

        vet_final: toNumber(vetFinal || vet || tmb) || null,
        vet_final_equation: 'FAO/OMS/1985',
        vet_final_weight: toNumber(vetFinalWeight) || null,
        vet_reduction_id: null, // ID da redução do VET, se aplicável

        zscore: toNumber(imcZscore) || null,


        // DEMAIS CAMPOS CONVERTIDOS (circunferências, dobras, etc)
        ...Object.fromEntries(
          [
            'abdominal_circumference', 'abdominal_skinfold', 'ankle_circumference', 'arm_circumference', 'axillary_skinfold', 'biceps_skinfold',
            'calf_circumference', 'calf_skinfold', 'chest_circumference', 'chest_skinfold', 'forearm_circumference',
            'gluteal_max_circumference', 'head_circumference', 'hip_circumference', 'subscapular_skinfold', 'supraespinal_skinfold',
            'suprailiac_skinfold', 'thigh_max_circumference', 'thigh_skinfold', 'triceps_skinfold', 'waist_circumference', 'wrist_circumference',
            'left_arm', 'right_arm', 'left_forearm', 'right_forearm', 'left_hand', 'right_hand', 'left_leg', 'right_leg',
            'left_foot', 'right_foot', 'left_lower_leg', 'right_lower_leg', 'overweight_duration', 'ppr_time', 'femur_diameter', 'humeral_diameter', 'radial_diameter',
            'vet_final',
          ].map((field) => [field, toNumber(values[field])])
        ),
      };
      // console.log('🚀 Payload final pronto para envio:', payload);
      if (!height || !weight || !gender || !age) {
        // setMessage("⚠️ Preencha todos os campos essenciais antes de enviar.");
        // setShowModal(true);
        // setTimeout(() => setShowModal(false), 2500);
        showInfo("⚠️ Preencha todos os campos essenciais antes de enviar.");
        setSubmitting(false);
        return;
      }
      const response = await dispatch(createNewAnthropometry(payload)).unwrap();
      // console.log("✅ Resposta da API:", response); // Deve exibir { message, id }

      if (response && response.id) {
        // setMessage(response.message || "Antropometria criada com sucesso!");
        // setShowModal(true);
        // setTimeout(() => {
        //   setShowModal(false);
        //   resetForm();
        //   navigate('/home/anthropometry/get');
        // }, 2000);
        showInfo(response.message || "Antropometria criada com sucesso!");
        resetForm();
        navigate('/home/anthropometry/get');
      } else {
        throw new Error(response?.message || 'Erro desconhecido ao salvar antropometria.');
      }
    } catch (error) {
      console.error("❌ Erro ao enviar formulário:", error.message);
      const errorMsg = error.response?.data?.message || error.message || 'Erro ao enviar formulário. Tente novamente mais tarde.';
      // setMessage(errorMsg);
      // setShowModal(true);
      // setTimeout(() => setShowModal(false), 2500);
      showInfo(errorMsg, 3000);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <>
      <div>
        <h3>Nova Antropometria</h3>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
        >
          {({ setFieldValue, isSubmitting, values, errors, touched }) => {
            setFieldValueRef.current = setFieldValue;
            return (
              <Form className='anthropometryForm'>
                {/* <div>
                  <div>Agendamento Id: <span className="spanDataTitle">{currentConsultation?.appointment_id}</span></div>
                  <div>Paciente Id: <span className="spanDataTitle">{currentConsultation?.patient_id}</span></div>
                  <div>Consulta Id: <span className="spanDataTitle">{currentConsultation?.consultation_id}</span></div>
                  <div>Diet Id: <span className="spanDataTitle">{currentConsultation?.diet_id}</span></div>
                </div> */}
                <div className="anthropometryMain">
                  <div className="anthropometryTabs">
                    <Tabs errors={errors} tabFieldMapping={tabFieldMapping}>
                      <Tab label="Nessa Medição">
                        {/* Paciente */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="patient_id">Paciente:</label>
                          <Select
                            name="patient_id"
                            options={persons
                              .filter(person => person.person_type === 'patient')
                              .map(patient => ({
                                value: patient.person_id,
                                label: `${patient.first_name} ${patient.last_name}`,
                              }))}
                            value={patients
                              .map(patient => ({ value: patient.person_id, label: patient.name }))
                              .find(option => option.value === values.patient_id)}
                            noOptionsMessage={() => "Nenhuma opção disponível"}
                            onChange={option => handlePatientChange(option, setFieldValue)}
                            classNamePrefix="custom-select"
                            placeholder="Selecione o paciente"
                          />
                          <ErrorMessage name="patient_id" component="div" className="error-message" />
                        </div>
                        {/* Nutricionista */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="nutritionist_id">Nutricionista:</label>
                          <Select
                            name="nutritionist_id"
                            options={selectOptions(professionals, 'name')}
                            value={selectOptions(professionals, 'name').find(
                              option => option.value === values.nutritionist_id
                            )}
                            noOptionsMessage={() => "Nenhuma opção disponível"}
                            onChange={option => setFieldValue('nutritionist_id', option ? option.value : '')}
                            classNamePrefix="custom-select"
                            placeholder="Selecione o nutricionista"
                          />
                          <ErrorMessage name="nutritionist_id" component="div" className="error-message" />
                        </div>
                        {/* Grupo do Paciente */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="patient_group_id">Grupo do Paciente:</label>
                          < Field
                            as="select"
                            name="patient_group_id"
                            id="patient_group_id"
                            onChange={(e) => {
                              const selectedGroup = e.target.value;
                              setFieldValue('patient_group_id', selectedGroup);
                              setSelectedPatientGroup(selectedGroup);

                              // Verifica se o paciente é criança e habilita o campo de zscore
                              if (selectedGroup === '2') {
                                setShowZScoreField(true);
                              } else {
                                setShowZScoreField(false);  // Oculta o campo para outros grupos
                              }
                            }}
                          >
                            <option value="">Selecione</option>
                            <option value="1">Lactente</option>
                            <option value="2">Criança</option>  {/* Grupo Criança */}
                            <option value="3">Adulto</option>
                            <option value="4">Idoso</option>
                            <option value="5">Gestante</option>
                            <option value="6">Lactante</option>
                          </Field >
                          <ErrorMessage name="patient_group_id" component="div" className="error-message" />
                          {erroGrupoGestante && <div className="error-message">{erroGrupoGestante}</div>}
                        </div >
                        {/* z-Score (somente crianças) */}
                        {showZScoreField && (
                          <ValidatedNumberInput
                            name="zscore"
                            label="z-Score"
                            min={-5}
                            max={5}
                            value={values.zscore}
                            setFieldValue={setFieldValue}
                            onChangeExtra={setImcZscore}
                          />

                        )}
                        {/* Data da Medição */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="measurement_date">Data da Medição:</label>
                          <Field type="date" name="measurement_date" id="measurement_date" />
                          <ErrorMessage name="measurement_date" component="div" className="error-message" />
                        </div>

                        {/* Campos Numéricos Padronizados */}
                        <ValidatedNumberInput name="height" label="Altura (cm)" min={40} max={250} value={values.height} setFieldValue={setFieldValue} onChangeExtra={setHeight} />
                        <ValidatedNumberInput name="current_weight" label="Peso Atual (kg)" min={0.5} max={300} value={values.current_weight} setFieldValue={setFieldValue} onChangeExtra={setWeight} />
                        <ValidatedNumberInput name="desired_weight" label="Peso Desejado (kg)" min={0.5} max={300} value={values.desired_weight} setFieldValue={setFieldValue} />
                        <ValidatedNumberInput name="usual_weight" label="Peso Habitual (kg)" min={0.5} max={300} value={values.usual_weight} setFieldValue={setFieldValue} />
                        <ValidatedNumberInput name="overweight_duration" label="Tempo de Sobrepeso (ano/meses)" min={0} max={120} value={values.overweight_duration} setFieldValue={setFieldValue} />
                        <ValidatedNumberInput name="ppr_time" label="Perda de Peso Recente - PPR" min={0} max={120} value={values.ppr_time} setFieldValue={setFieldValue} />

                        {/* Atividade Física */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="physical_activity_frequency">Atividade Física:</label>
                          <Field
                            as="select"
                            name="physical_activity_frequency"
                            id="physical_activity_frequency"
                            onChange={(e) => {
                              setFieldValue('physical_activity_frequency', e.target.value);
                              setFrequencyOfActivity(e.target.value); // Atualiza o estado da physicalActivity
                            }}>
                            <option value="">Selecione</option>
                            <option value="unable">Nenhuma</option>
                            <option value="sedentary">Sedentario</option>
                            <option value="occasional">Ocasional</option>
                            <option value="frequent">Frequente</option>
                            <option value="regular">Regular</option>
                          </Field>
                          <ErrorMessage name="physical_activity_frequency" component="div" className="error-message" />
                        </div>
                        {/* Fator de Atividade Física */}
                        <div className="anthropometry-form-group">
                          <label htmlFor="physical_activity_level">Nível da Atividade Física:</label>
                          <Field
                            as="select"
                            name="physical_activity_level"
                            id="physical_activity_level"
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue('physical_activity_level', e.target.value);
                              setPhysicalActivityLevel(e.target.value); // Atualiza o estado da physicalActivityLevel
                              // Atualiza automaticamente o código do fator
                              if (activityFactorCodeMap[value]) {
                                setFieldValue('activity_factor_code', activityFactorCodeMap[value]);
                              }
                            }}>
                            <option value="">Selecione</option>
                            <option value="light">Leve</option>
                            <option value="moderate">Moderada</option>
                            <option value="intense">Intensa</option>
                          </Field>
                          <ErrorMessage name="physical_activity_level" component="div" className="error-message" />
                        </div>
                        {/* Fator de Atividade Física - Código (somente leitura) */}
                        <div className="anthropometry-form-group hiddenField">
                          <label htmlFor="activity_factor_code">Código do Fator de Atividade Física:</label>
                          <Field
                            type="number"
                            name="activity_factor_code"
                            id="activity_factor_code"
                            onKeyDown={(e) => onlyNumbers(e, setFieldValue, 'activity_factor_code')}
                            onChange={(e) => setFieldValue('activity_factor_code', e.target.value)}
                            readOnly
                            disabled

                          />
                          <ErrorMessage name="activity_factor_code" component="div" className="error-message" />
                        </div>



                        {/* Verifica se o paciente é do gênero feminino */}

                        {selectedPatientGender === 'female' && (
                          <>
                            {/* Exibe o campo de DUM para todas as pacientes do gênero feminino */}
                            <div className="anthropometry-form-group">
                              <label htmlFor="last_period_date">Data da Última Menstruação - DUM:</label>
                              <Field
                                type="date"
                                name="last_period_date"
                                id="last_period_date"
                                onChange={(e) => {
                                  const dumValue = e.target.value;
                                  setFieldValue('last_period_date', dumValue);
                                  setDum(dumValue);
                                  handleGestanteCalculations(dumValue); // Calcula IG e DPP automaticamente
                                }}
                              />
                              <ErrorMessage name="last_period_date" component="div" className="error-message" />
                            </div>

                            {/* Verifica se o grupo do paciente é "Gestante" */}
                            {selectedPatientGroup === '5' && (
                              <>
                                {/* Campo calculado para Idade Gestacional (não editável) */}
                                <div className="anthropometry-form-group">
                                  <label htmlFor="gestational_age">Idade Gestacional - IG (semanas):</label>
                                  <Field
                                    type="text"
                                    name="gestational_age"
                                    id="gestational_age"
                                    value={idadeGestacional || ''} // Valor calculado
                                    readOnly
                                  />
                                  <ErrorMessage name="gestational_age" component="div" className="error-message" />
                                </div>

                                {/* Campo calculado para Data Prevista do Parto (não editável) */}
                                <div className="anthropometry-form-group">
                                  <label htmlFor="dpp_date">Data Prevista do Parto - DPP:</label>
                                  <Field
                                    type="text"
                                    name="dpp_date"
                                    id="dpp_date"
                                    value={dppDate || ''} // Valor calculado
                                    disabled
                                  />
                                  <ErrorMessage name="dpp_date" component="div" className="error-message" />
                                </div>

                                {/* Campo de Peso Pré Gestacional (input manual) */}
                                <ValidatedNumberInput name="ppg_weight" label="Peso Pré Gestacional - PPG (kg)" min={0.5} max={300} value={values.ppg_weight} setFieldValue={setFieldValue} onChangeExtra={setPpgWeight} />
                                {/* Campo de Ganho de Peso Recomendado */}
                                <ValidatedNumberInput name="recommended_weight_gain" label="Ganho de Peso Recomendado - GPR (kg)" min={0} max={30} value={values.recommended_weight_gain} setFieldValue={setFieldValue} onChangeExtra={setRecommendedWeightGain} />

                                {/* IMC Gestacional (calculado automaticamente) */}
                                <div className="anthropometry-form-group">
                                  <label htmlFor="gestational_imc">IMC - Gestacional:</label>
                                  <Field
                                    type="text"
                                    name="gestational_imc"
                                    id="gestational_imc"
                                    value={gestationalImc || ''} // IMC calculado
                                    disabled
                                  />
                                  <ErrorMessage name="gestational_imc" component="div" className="error-message" />
                                </div>
                              </>
                            )}
                          </>
                        )
                        }
                        {/* <Escalometro biotipoIndex={classificarBiotipo} /> */}

                      </Tab>

                      <Tab label="2 - Circunferências">
                        <ValidatedNumberInput
                          name="head_circumference"
                          label="Circunferência da Cabeça (cm):"
                          min={10}
                          max={200}
                          value={values.head_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setHeadCircumference}
                        />

                        <ValidatedNumberInput
                          name="arm_circumference"
                          label="Circunferência do Braço (cm):"
                          min={10}
                          max={200}
                          value={values.arm_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setArmCircumference}
                        />

                        <ValidatedNumberInput
                          name="waist_circumference"
                          label="Circunferência da Cintura (cm):"
                          min={10}
                          max={200}
                          value={values.waist_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setWaistCircumference}
                        />

                        <ValidatedNumberInput
                          name="chest_circumference"
                          label="Circunferência do Tórax (cm):"
                          min={10}
                          max={200}
                          value={values.chest_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setChestCircumference}
                        />

                        <ValidatedNumberInput
                          name="abdominal_circumference"
                          label="Circunferência Abdominal (cm):"
                          min={10}
                          max={200}
                          value={values.abdominal_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setAbdominalCircumference}
                        />

                        <ValidatedNumberInput
                          name="hip_circumference"
                          label="Circunferência do Quadril (cm):"
                          min={10}
                          max={200}
                          value={values.hip_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setHipCircumference}
                        />

                        <ValidatedNumberInput
                          name="gluteal_max_circumference"
                          label="Circunferência Máxima do Glúteo (cm):"
                          min={10}
                          max={200}
                          value={values.gluteal_max_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setGlutealMaxCircumference}
                        />

                        <ValidatedNumberInput
                          name="thigh_max_circumference"
                          label="Circunferência Máxima da Coxa (cm):"
                          min={10}
                          max={200}
                          value={values.thigh_max_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setThighMaxCircumference}
                        />

                        <ValidatedNumberInput
                          name="forearm_circumference"
                          label="Circunferência Máxima do Antebraço (cm):"
                          min={10}
                          max={200}
                          value={values.forearm_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setForearmCircumference}
                        />

                        <ValidatedNumberInput
                          name="wrist_circumference"
                          label="Circunferência do Pulso (cm):"
                          min={10}
                          max={200}
                          value={values.wrist_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setWristCircumference}
                        />

                        <ValidatedNumberInput
                          name="calf_circumference"
                          label="Circunferência da Panturrilha (cm):"
                          min={10}
                          max={200}
                          value={values.calf_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setCalfCircumference}
                        />

                        <ValidatedNumberInput
                          name="ankle_circumference"
                          label="Circunferência do Tornozelo (cm):"
                          min={10}
                          max={100}
                          value={values.ankle_circumference}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setAnkleCircumference}
                        />
                      </Tab>

                      <Tab label="3 - Braços / Pernas">
                        <div className="arms_legs_box">
                          <div className="column_arms">
                            <ValidatedNumberInput
                              name="left_arm"
                              label="Braço Esquerdo (cm):"
                              min={10}
                              max={200}
                              value={values.left_arm}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setLeftArmCircumference}
                            />


                            <ValidatedNumberInput
                              name="right_arm"
                              label="Braço Direito (cm):"
                              min={10}
                              max={200}
                              value={values.right_arm}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setRightArmCircumference}
                            />

                            <ValidatedNumberInput
                              name="left_forearm"
                              label="Antebraço Esquerdo (cm):"
                              min={10}
                              max={200}
                              value={values.left_forearm}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setLeftForeArmCircumference}
                            />

                            <ValidatedNumberInput
                              name="right_forearm"
                              label="Antebraço Direito (cm):"
                              min={10}
                              max={200}
                              value={values.right_forearm}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setRightForeArmCircumference}
                            />

                            <ValidatedNumberInput
                              name="left_hand"
                              label="Mão Esquerda (cm):"
                              min={10}
                              max={200}
                              value={values.left_hand}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setLeftHandCircumference}
                            />

                            <ValidatedNumberInput
                              name="right_hand"
                              label="Mão Direita (cm):"
                              min={10}
                              max={200}
                              value={values.right_hand}
                              setFieldValue={setFieldValue}
                              onChangeExtra={setRightHandCircumference}
                            />
                          </div>
                          <div className="column_legs">
                            <ValidatedNumberInput
                              name="left_leg"
                              label="Perna Esquerda (cm):"
                              min={10}
                              max={150}
                              value={values.left_leg}
                              setFieldValue={setFieldValue}
                            />

                            <ValidatedNumberInput
                              name="right_leg"
                              label="Perna Direita (cm):"
                              min={10}
                              max={150}
                              value={values.right_leg}
                              setFieldValue={setFieldValue}
                            />

                            <ValidatedNumberInput
                              name="left_lower_leg"
                              label="Perna Inferior Esquerda (cm):"
                              min={10}
                              max={100}
                              value={values.left_lower_leg}
                              setFieldValue={setFieldValue}
                            />

                            <ValidatedNumberInput
                              name="right_lower_leg"
                              label="Perna Inferior Direita (cm):"
                              min={10}
                              max={100}
                              value={values.right_lower_leg}
                              setFieldValue={setFieldValue}
                            />

                            <ValidatedNumberInput
                              name="right_foot"
                              label="Pé Esquerdo (cm):"
                              min={5}
                              max={40}
                              value={values.right_foot}
                              setFieldValue={setFieldValue}
                            />

                            <ValidatedNumberInput
                              name="left_foot"
                              label="Pé Direito (cm):"
                              min={5}
                              max={40}
                              value={values.left_foot}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        </div>
                      </Tab>

                      <Tab label="4 - Diâmetros Ósseos">
                        <ValidatedNumberInput
                          name="radial_diameter"
                          label="Diâmetro do Radial (cm):"
                          min={1}
                          max={10}
                          value={values.radial_diameter}
                          setFieldValue={setFieldValue}
                        />

                        <ValidatedNumberInput
                          name="femur_diameter"
                          label="Diâmetro do Fêmur (cm):"
                          min={1}
                          max={10}
                          value={values.femur_diameter}
                          setFieldValue={setFieldValue}
                        />

                        <ValidatedNumberInput
                          name="humeral_diameter"
                          label="Diâmetro do Úmero (cm):"
                          min={1}
                          max={10}
                          value={values.humeral_diameter}
                          setFieldValue={setFieldValue}
                        />

                        <ValidatedNumberInput
                          name="knee_height"
                          label="Altura do Joelho (cm):"
                          min={20}
                          max={60}
                          value={values.knee_height}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setKneeHeight}
                        />
                      </Tab>

                      <Tab label="5 - Dobras Cutâneas">
                        <ValidatedNumberInput
                          name="suprailiac_skinfold"
                          label="Dobra Suprailíaca (mm):"
                          min={2}
                          max={70}
                          value={values.suprailiac_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setSuprailiacSkinfold}
                        />

                        <ValidatedNumberInput
                          name="subscapular_skinfold"
                          label="Dobra Subescapular (mm):"
                          min={5}
                          max={70}
                          value={values.subscapular_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setSubscapularSkinfold}
                        />

                        <ValidatedNumberInput
                          name="supraespinal_skinfold"
                          label="Dobra Supraespinhal (mm):"
                          min={5}
                          max={70}
                          value={values.supraespinal_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setSupraespinalSkinfold}
                        />

                        <ValidatedNumberInput
                          name="chest_skinfold"
                          label="Dobra Peitoral (mm):"
                          min={5}
                          max={70}
                          value={values.chest_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setChestSkinfold}
                        />

                        <ValidatedNumberInput
                          name="triceps_skinfold"
                          label="Dobra Tríceps (mm):"
                          min={5}
                          max={70}
                          value={values.triceps_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setTricepsSkinfold}
                        />

                        <ValidatedNumberInput
                          name="biceps_skinfold"
                          label="Dobra Bíceps (mm):"
                          min={2}
                          max={70}
                          value={values.biceps_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setBicepsSkinfold}
                        />

                        <ValidatedNumberInput
                          name="thigh_skinfold"
                          label="Dobra da Coxa (mm):"
                          min={5}
                          max={70}
                          value={values.thigh_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setThighSkinfold}
                        />

                        <ValidatedNumberInput
                          name="calf_skinfold"
                          label="Dobra da Panturrilha (mm):"
                          min={2}
                          max={70}
                          value={values.calf_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setCalfSkinfold}
                        />

                        <ValidatedNumberInput
                          name="abdominal_skinfold"
                          label="Dobra Abdominal (mm):"
                          min={5}
                          max={70}
                          value={values.abdominal_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setAbdominalSkinfold}
                        />

                        <ValidatedNumberInput
                          name="axillary_skinfold"
                          label="Dobra Axilar (mm):"
                          min={5}
                          max={70}
                          value={values.axillary_skinfold}
                          setFieldValue={setFieldValue}
                          onChangeExtra={setAxillarySkinfold}
                        />

                        <div className="anthropometry-form-group">
                          <label htmlFor="fat_calculate_equation">Equação do Cálculo de Gordura:</label>
                          <Field
                            as="select"
                            name="fat_calculate_equation"
                            id="fat_calculate_equation"
                            onChange={(e) => {
                              const selectedFatEquation = e.target.value;
                              const selectedText = e.target.options[e.target.selectedIndex].text;
                              setEquationText(selectedText);
                              setFieldValue('fat_calculate_equation', selectedFatEquation);
                              setSelectedFatPercentageEquation(selectedFatEquation);
                            }}
                          >
                            <option value="">Selecione</option>
                            <option value="1">3 Dobras (18 - 60 anos) - Jackson e Pollock (1978)</option>
                            <option value="2">7 Dobras (18 - 60 anos) - Jackson e Pollock (1978)</option>
                            <option value="3">4 Dobras (18 - 60 anos) - Petrosky (1995)</option>
                            <option value="4">4 Dobras (Homens 17 - 72 / Mulheres 16 - 68) - Durnin & Womersley (1974)</option>
                          </Field>
                          <ErrorMessage name="fat_calculate_equation" component="div" className="error-message" />
                        </div>

                        <div className="anthropometry-form-group">
                          <label htmlFor="diagnosis">Diagnóstico:</label>
                          <Field
                            as="textarea"
                            name="diagnosis"
                            id="diagnosis"
                            onChange={(e) => {
                              setFieldValue('diagnosis', e.target.value);
                              setDiagnosis(e.target.value);
                            }}
                          />
                          <ErrorMessage name="diagnosis" component="div" className="error-message" />
                        </div>
                      </Tab>
                    </Tabs>

                  </div>

                  <div className="anthropometry-colC">
                    <div className="formButtons">
                      <button className='sendBtn' type="submit" disabled={isSubmitting}>Enviar</button>
                      {/* <button className='backBtn' type="button" onClick={() => navigate('/home/anthropometry/get')}>Cancelar</button> */}
                      <button
                        className="backBtn"
                        type="button"
                        onClick={() => {
                          if (currentConsultation.consultation_id) {
                            navigate(`/home/consultation/show/${currentConsultation.consultation_id}`);
                          } else {
                            navigate('/home/anthropometry/get/withFilters');
                          }
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>

                </div >
              </Form >
            );
          }}
        </Formik >
        {/* {showModal && (
          <ModalForms
            message={message}
            onConfirm={() => setShowModal(false)}
          />
        )} */}
      </div >
    </>
  );

};

export default CreateAnthropometry;